import { useMsal } from "@azure/msal-react";
import { GlobalWorkerOptions } from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.entry';
import React, {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { v4 as uuidv4 } from "uuid";
import {
  Button,
  Form,
  OverlayTrigger,
  Tooltip
} from "react-bootstrap"; // Import OverlayTrigger, Tooltip, and ProgressBar for UI elements
import { chatInputConfig } from "../../../config/componentsConfig";
import { useChatContext } from "../../../contexts/ChatContext"; // Custom hook for chat context
import {
  TaskStatus,
  TaskType,
  useTasksContext,
} from "../../../contexts/TasksContext";
import { shouldAttachLegalSearchTask } from "../../../utils/legalSearchTaskEligibility";
import {
  LEGAL_PENDING_TASK_SESSION_KEY,
  LEGAL_PENDING_TASK_THREAD_SESSION_KEY,
  createLegalSearchTaskFromQuery,
  emitLegalTaskAdded,
  emitLegalTaskProgressCompleted,
  clearPendingLegalTaskSession,
  setLegalActiveSseTaskId,
  setPendingLegalTaskId,
  setPendingLegalTaskThreadId,
} from "../../../utils/legalSearchTaskFlow";
import useFetchWithMsal from "../../../hooks/useFetchWithMsal";
import useIsMobile from "../../../hooks/useIsMobile";
import styles from "./ChatInput.module.scss";
import DiscoverGeorgesModal from "./DiscoverGeorges/DiscoverGeorgesModal";
import DisplaySourcesCheckbox from "./DisplaySourcesCheckbox";
import AnswerFormatModal from "./AnswerFormatModal";
import type { AnswerFormat, RagReasoningPace } from "../../../utils/answerFormat";
import {
  ANSWER_FORMAT,
  shouldShowAnswerFormatModal,
} from "../../../utils/answerFormat";
import {
  cancelExhaustiveSearchJob,
  resolveExhaustiveTaskIdForThread,
} from "../../../utils/legalSearchExhaustiveCancel";
import { isCarousel } from "../../../types/typeGuards";

// Define interfaces for content structure
interface Square {
  title: string;
  text: string;
  icon: string;
  mode: string;
  filters: Record<string, unknown>;
}

interface Container {
  key: string;
  title: string;
  tooltip: string;
  modeValue: string;
  icon: string;
  label: string;
  squares: Square[];
}

interface ContentPage {
  containers: Container[];
}

interface Content {
  market: ContentPage;
  legal: ContentPage;
  mydeal: ContentPage;
  legaloreima: ContentPage;
}



// Define the props interface for the ChatInput component
interface ChatInputProps {
  page: string; 
  filters: object; 
  selectedText: string; 
  selectedMode: string; 
  /** Panneau latéral actif (ex. 5 = My Tasks) — évite de fermer le menu à la saisie */
  selectedMenu?: number;
  setSelectedMode: (mode: string) => void; 
  setSelectedMenu: (menu: number) => void;
  modeOptions: Array<{
    value: string; 
    icon: string; 
    label: string; 
    tooltip: string; 
  }>; 
  displaySources: boolean;
  setDisplaySources: (value: boolean) => void;
  displayCarousel: boolean;
  setDisplayCarousel: (value: boolean) => void;
  setIsUpdatingFiles: (value: boolean) => void;
  devMode: boolean;
  setDevMode: (value: boolean) => void;
}

// Define the props interface for MemoizedTextArea
interface MemoizedTextAreaProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyDown: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  isLoadingQuery: boolean;
  isReformulation: boolean;
  inputRef: React.RefObject<HTMLTextAreaElement>;
}

// Define the props interface for MemoizedSendButton
interface MemoizedSendButtonProps {
  isReformulation: boolean;
  onClick: () => void;
  onAbort: () => void;
}

// Define the props interface to fetch do processing status for mygeorges
interface ProcessingStatus {
  files: Array<{
    filename: string;
    nbPages: number;
    nbProcessed: number;
    status: string;
  }>;
}

// worker PDF.js utilisé pour calculer totalPages 
GlobalWorkerOptions.workerSrc = pdfjsWorker;

//------COMPONENTS------

const MemoizedTextArea = memo(({ 
  value, 
  onChange, 
  onKeyDown, 
  isLoadingQuery, 
  isReformulation,
  inputRef 
}: MemoizedTextAreaProps) => {
  /**
 * Composant mémorisé pour la zone de texte
 * Gère l'affichage et le redimensionnement automatique de la zone de saisie
 */
  // Utiliser useLayoutEffect pour synchroniser avec le DOM
  useLayoutEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      textarea.style.height = "auto";
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  }, [value]);

  return (
    <textarea
      ref={inputRef}
      // Autoriser la saisie même quand une réponse est en cours.
      // L'envoi reste bloqué plus bas dans openSendFlow/commitSend.
      disabled={false}
      id="inlineFormInputGroup"
      placeholder={isReformulation ? "Reformulating..." : "Ask anything to Georges"}
      value={value || ""}
      onChange={onChange}
      onKeyDown={onKeyDown}
      className={styles.textarea}
    />
  );
});


const MemoizedSendButton = memo(({ isReformulation, onClick, onAbort }: MemoizedSendButtonProps) => {
  const handleClick = () => {
    if (isReformulation) {
      onAbort();
    } else {
      onClick();
    }
  };

  return (
    <Button variant="light" className={styles.sendButton} onClick={handleClick}>
      <i className={`bi ${isReformulation ? 'bi-stop' : 'bi-arrow-up'}`} style={{color: "white"}}></i>
    </Button>
  );
});


// Nouveau composant pour la barre de progression
/*interface UploadProgressBarProps {
  userId: string;
  onComplete?: (status: ProcessingStatus) => void;
  processingStatus: ProcessingStatus;
  onStatusUpdate: (status: ProcessingStatus) => void;
  isProcessing: boolean;
}

const UploadProgressBar = memo(({ userId, onComplete, processingStatus, onStatusUpdate, isProcessing }: UploadProgressBarProps) => {
  const { execute } = useFetchWithMsal();
  const completedRef = useRef(false);
  const completedFilesRef = useRef(new Set<string>());
  

  useEffect(() => {
    let pollingInterval: NodeJS.Timeout;
    
    const checkProcessingStatus = async () => {
      try {
        if (!processingStatus?.files?.length) return;

        console.log('🔍 Vérification du statut pour les fichiers:', processingStatus.files.map(f => f.filename));

        const updatedFiles = await Promise.all(processingStatus.files.map(async (file) => {
          if (completedFilesRef.current.has(file.filename)) {
            console.log(`⏭️ Fichier déjà traité, on le saute: ${file.filename}`);
            return null;
          }

          try {
            console.log(`📡 Requête get_processing_status pour ${file.filename}`);
            const response = await execute('POST', endpointsCf.docprocessbar(userId), {
              filename: file.filename
            });
            console.log(`📥 Réponse get_processing_status pour ${file.filename}:`, response);

            if (!response || typeof response !== 'object') {
              console.log(`⚠️ Réponse invalide pour ${file.filename}:`, response);
              return file;
            }

            // Créer un nouvel objet avec les données mises à jour
            const updatedFile = {
              filename: response.filename,
              nbPages: response.nbPages,
              nbProcessed: response.nbProcessed,
              status: response.status
            };

            if (response.status === 'completed') {
              console.log(`✅ Fichier complètement traité: ${file.filename}`);
              completedFilesRef.current.add(file.filename);
              onComplete?.({
                files: [updatedFile]
              });
              return null;
            }

            return updatedFile;
          } catch (error) {
            console.error(`❌ Erreur pour ${file.filename}:`, error);
            return file;
          }
        }));

        const remainingFiles = updatedFiles.filter((file): file is NonNullable<typeof file> => 
          file !== null && !completedFilesRef.current.has(file.filename)
        );

        if (remainingFiles.length > 0) {
          onStatusUpdate({ files: remainingFiles });
        } else {
          console.log('🏁 Plus de fichiers à traiter, arrêt du polling');
          completedRef.current = true;
          if (pollingInterval) {
            clearInterval(pollingInterval);
          }
        }
      } catch (error) {
        console.error('❌ Erreur lors de la vérification du statut:', error);
      }
    };

    if (isProcessing && !completedRef.current && processingStatus?.files?.length > 0) {
      console.log('🔄 Démarrage du polling pour:', processingStatus.files.map(f => f.filename));
      pollingInterval = setInterval(checkProcessingStatus, 5000);
    }

    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [isProcessing, processingStatus?.files, userId, execute, onComplete, onStatusUpdate]);

  if (!isProcessing || !processingStatus?.files?.length) return null;

  // Afficher uniquement les fichiers non complétés
  const activeFiles = processingStatus.files.filter(
    file => !completedFilesRef.current.has(file.filename)
  );

  if (activeFiles.length === 0) return null;

  // Avant le return, ajoutons un log pour voir le format des données
  console.log('🔄 Données de la barre de progression:', {
    activeFiles: processingStatus.files.map(file => ({
      filename: file.filename,
      nbPages: file.nbPages,
      nbProcessed: file.nbProcessed,
      status: file.status
    }))
  });

  return (
    <div className={styles.progressContainer}>
      <div className={styles.progressHeader}>
        <span>Traitement des documents ({processingStatus.files.length})</span>
      </div>
      {processingStatus.files.map((file) => (
        <div key={file.filename} className={styles.fileProgress}>
          <div className={styles.fileInfo}>
            <span className={styles.fileName}>{file.filename}</span>
            <span className={styles.pageCount}>
              {file.nbPages > 0 ? `${file.nbProcessed}/${file.nbPages} pages` : 'Calcul des pages...'}
            </span>
          </div>
          <ProgressBar
            now={file.nbPages > 0 ? (file.nbProcessed / file.nbPages) * 100 : 0}
            label={file.nbPages > 0 ? `${Math.round((file.nbProcessed / file.nbPages) * 100)}%` : 'En attente...'}
            variant={file.status === 'completed' ? "success" : "primary"}
          />
        </div>
      ))}
    </div>
  );
});

interface FileUploadInfo {
  files: Array<{
    filename: string;
    totalPages: number;
  }>;
}*/

interface MemoizedControlsProps {
  selectedMode: string;
  modeOptions: Array<{
    value: string;
    icon: string;
    label: string;
    tooltip: string;
  }>;
  exhaustiveSearchEnabled: boolean;
  setExhaustiveSearchEnabled: (v: boolean) => void;
  setSelectedMode: (mode: string) => void;
  setSelectedMenu: (menu: number) => void;
  isMobile: boolean;
  optionIcons: any;
  page: string;
  displayCarousel: boolean;
  setDisplayCarousel: (value: boolean) => void;
  onFileUpload: (fileInfo: FileUploadInfo) => void;
  isProcessing: boolean;
  processingStatus: ProcessingStatus | null;
  onProcessingComplete: () => void;
  onStatusUpdate: (status: ProcessingStatus) => void;
  displaySources: boolean;
  setDisplaySources: (value: boolean) => void;
  devMode: boolean;
  setDevMode: (value: boolean) => void;
  exhaustiveSequentialClassic: boolean;
  setExhaustiveSequentialClassic: (value: boolean) => void;
  onResynthesizeExhaustiveFinal?: () => void;
  canResynthesizeExhaustiveFinal?: boolean;
  resynthesizeFinalBusy?: boolean;
  exhaustiveCapablePage?: boolean;
  showDevButtons?: boolean;
}

const MemoizedControls = memo(({ 
  selectedMode, 
  modeOptions, 
  exhaustiveSearchEnabled,
  setExhaustiveSearchEnabled,
  setSelectedMode, 
  setSelectedMenu,
  isMobile, 
  optionIcons, 
  page, 
  displayCarousel,
  setDisplayCarousel,
  onFileUpload,
  isProcessing,
  processingStatus,
  onProcessingComplete,
  onStatusUpdate,
  displaySources,
  setDisplaySources,
  devMode,
  setDevMode,
  exhaustiveSequentialClassic,
  setExhaustiveSequentialClassic,
  onResynthesizeExhaustiveFinal,
  canResynthesizeExhaustiveFinal = false,
  resynthesizeFinalBusy = false,
  exhaustiveCapablePage = false,
  showDevButtons = false,
}: MemoizedControlsProps) => {
  const { execute } = useFetchWithMsal();
  const { instance } = useMsal();
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const [currentFiles, setCurrentFiles] = useState<Array<{filename: string; totalPages: number}>>([]);
  const activeAccount = instance.getActiveAccount();
  const [showDiscoverModal, setShowDiscoverModal] = useState(false);
  const COPILOT_SSO_URL = "https://m365.cloud.microsoft/chat/?auth=2&home=1&fromcode=cmm6e0ny53n";
  const PERPLEXITY_SSO_URL = "https://launcher.myapps.microsoft.com/api/signin/52a3b076-ec4b-40ce-92d7-018449b768d3?tenantId=7fc6be4a-81b6-457d-9ad7-c8d5c64d052c";
  const openInNewTab = (url: string) => window.open(url, "_blank", "noopener,noreferrer");

  // Modifier la fonction getTotalPages dans MemoizedControls
  /*const getTotalPages = async (file: File): Promise<number> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          if (!e.target?.result) {
            console.error('❌ Erreur de lecture du fichier');
            resolve(0);
            return;
          }

          const arr = new Uint8Array(e.target.result as ArrayBuffer);
          
          try {
            const loadingTask = getDocument({ data: arr });
            const pdf = await loadingTask.promise;
            console.log(`📚 Nombre de pages détecté pour ${file.name}: ${pdf.numPages}`);
            resolve(pdf.numPages);
          } catch (error) {
            console.error('❌ Erreur lors de la lecture du PDF:', error);
            
            try {
              const blob = new Blob([arr], { type: 'application/pdf' });
              const url = URL.createObjectURL(blob);
              const loadingTask = getDocument(url);
              const pdf = await loadingTask.promise;
              console.log(`📚 Nombre de pages détecté (2ème tentative) pour ${file.name}: ${pdf.numPages}`);
              URL.revokeObjectURL(url);
              resolve(pdf.numPages);
            } catch (error2) {
              console.error('❌ Échec de la lecture du PDF');
              resolve(0);
            }
          }
        } catch (error) {
          console.error('❌ Erreur générale:', error);
          resolve(0);
        }
      };

      reader.onerror = (error) => {
        console.error('❌ Erreur de lecture:', error);
        resolve(0);
      };

      reader.readAsArrayBuffer(file);
    });
  };


  const handleFileUpload = async (files: FileList) => {
    if (!files.length || !activeAccount) return;

      const formData = new FormData();
    formData.append('userId', activeAccount.username);
      
    console.log('📁 Fichiers sélectionnés:', Array.from(files).map(f => f.name));

      const filesWithPages = await Promise.all(
        Array.from(files).map(async (file) => {
        const totalPages = await getTotalPages(file);
        
        if (totalPages === 0) {
          console.error(`❌ Erreur: Impossible de lire le nombre de pages pour ${file.name}`);
          return null;
        }
          return {
            file,
          totalPages
          };
        })
      );

    // Filtrer les fichiers qui n'ont pas pu être lus
    const validFiles = filesWithPages.filter((f): f is NonNullable<typeof f> => f !== null);
    
    if (validFiles.length === 0) {
      console.error('❌ Aucun fichier n\'a pu être lu correctement');
      return;
    }

    validFiles.forEach(({ file }) => {
        formData.append('files', file);
      });

      const processFiles = validFiles.map(({ file, totalPages }) => ({
            filename: file.name,
        totalPages,
        processed: 0
      }));
          
      setCurrentFiles(processFiles);
      setShowUploadProgress(true);
      onFileUpload({
        files: processFiles
      });

      try {
        const uploadResponse = await execute('POST', endpointsCf.upload, formData);
        console.log('⬆️ Réponse upload:', uploadResponse);
          
        

      } catch (error) {
      console.error('❌ Erreur lors de l\'upload:', error);
      }
  };*/

  return (
    <>
        <div className={styles.buttonGroup}>
          {modeOptions
            .filter((option) => option.value !== "UNDEFINED")
            .map((option) => (
            <OverlayTrigger
              key={option.value}
              placement="top"
              delay={{ show: 400, hide: 200 }}
              overlay={
                <Tooltip id={`tooltip-${option.value}`}>
                  {option.tooltip}
                </Tooltip>
              }
            >
              <Button 
                variant="light" 
                onClick={() => {
                  if (option.value === "CHAT") {
                    openInNewTab(COPILOT_SSO_URL);
                    return;
                  }
                  if (option.value === "PERPLEXITY") {
                    openInNewTab(PERPLEXITY_SSO_URL);
                    return;
                  }
                  // Un seul mode à la fois ; sortie d’Exhaustive dès qu’on choisit un autre mode liste.
                  setExhaustiveSearchEnabled(false);
                  setSelectedMode(option.value);
                }}
                active={
                  option.value === "SEARCH"
                    ? selectedMode === "SEARCH" && !exhaustiveSearchEnabled
                    : selectedMode === option.value
                }
                className={styles.customButton}
              >
                <i className={optionIcons[option.value as keyof typeof optionIcons].icon} style={{color: "grey"}}></i>
                {!isMobile && optionIcons[option.value as keyof typeof optionIcons].label}
              </Button>
            </OverlayTrigger>
          ))}
          {["market", "mydeal", "legal", "oreima"].includes(page) && (
            <OverlayTrigger
              placement="top"
              delay={{ show: 400, hide: 200 }}
              overlay={
                <Tooltip id="tooltip-exhaustive-search">
                  ExhaustiveSearch — multi-doc : pipeline structuré (grille / synthèse). Option
                  dev « classique » : ancien carrousel + macro.
                </Tooltip>
              }
            >
              <Button
                variant={exhaustiveSearchEnabled ? "secondary" : "light"}
                onClick={() => {
                  const next = !exhaustiveSearchEnabled;
                  setExhaustiveSearchEnabled(next);
                  if (!next) {
                    setExhaustiveSequentialClassic(false);
                  }
                  if (next) {
                    setSelectedMode("SEARCH");
                  }
                }}
                active={exhaustiveSearchEnabled}
                className={styles.customButton}
              >
                <i className="bi bi-diagram-3" style={{ color: "grey" }}></i>
                {!isMobile && "ExhaustiveSearch"}
              </Button>
            </OverlayTrigger>
          )}
        {/*selectedMode === 'MYGEORGES' && (
          <OverlayTrigger
            placement="top"
            delay={{ show: 400, hide: 200 }}
            overlay={
              <Tooltip id="tooltip-upload">
                Upload documents
              </Tooltip>
            }
          >
            <Button 
              variant="light"
              className={styles.plusButton}
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.multiple = true;
                input.onchange = (e) => handleFileUpload((e.target as HTMLInputElement).files!);
                input.click();
              }}
            >
              <i className="bi bi-plus" style={{color: "grey"}}></i>
            </Button>
          </OverlayTrigger>
        )}*/}
        {!isMobile && ( //On Computer, display a checkbox to display sources
          <>
            <div style={{display: "flex", flexDirection: "row", flex: 1, width: "100%", justifyContent: "flex-end", alignItems: "center", gap: "1rem"}}>
              <DisplaySourcesCheckbox
                label="Display sources"
                checked={displaySources}
                onChange={setDisplaySources}
              />
              {showDevButtons && (
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 400, hide: 200 }}
                  overlay={
                    <Tooltip id="tooltip-dev-tools">
                      Options avancées du chat: active les réglages techniques liés au
                      pipeline exhaustif et aux tâches.
                    </Tooltip>
                  }
                >
                  <span>
                    <DisplaySourcesCheckbox
                      label="Outils dev"
                      checked={devMode}
                      onChange={setDevMode}
                    />
                  </span>
                </OverlayTrigger>
              )}
              {exhaustiveSearchEnabled && showDevButtons && (
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 400, hide: 200 }}
                  overlay={
                    <Tooltip id="tooltip-exhaustive-sequential">
                      Active l&apos;ancien flux exhaustif (carousel + macro,
                      `exhaustive_search_mode=classic`) et force le traitement un document
                      à la fois (`exhaustive_sequential`).
                    </Tooltip>
                  }
                >
                  <span>
                    <DisplaySourcesCheckbox
                      label="1 doc à la fois (classique)"
                      checked={exhaustiveSequentialClassic}
                      onChange={setExhaustiveSequentialClassic}
                    />
                  </span>
                </OverlayTrigger>
              )}
              {page === "market" && !isMobile && <button className={styles.discoverButton} onClick={() => setShowDiscoverModal(!showDiscoverModal)}>
                <i className="bi bi-stars"></i>
                Discover Georges
                <DiscoverGeorgesModal setSelectedMode={setSelectedMode} setSelectedMenu={setSelectedMenu} showDiscoverModal={showDiscoverModal} setShowDiscoverModal={setShowDiscoverModal}/>
              </button>}
            </div>
          </>
        )}
      </div>
      {/*showUploadProgress && activeAccount && processingStatus && (
        <div className={styles.activeProgressWrapper}>
          <UploadProgressBar 
            key={processingStatus.files.map(f => f.filename).join(',')}
            userId={activeAccount.username}
            onComplete={onProcessingComplete}
            processingStatus={processingStatus}
            onStatusUpdate={onStatusUpdate}
            isProcessing={isProcessing}
          />
        </div>
      )}*/}
    </>
  );
});


/**
 * Composant principal de saisie du chat
 * Gère la saisie utilisateur, les différents modes de conversation et l'envoi des requêtes
 */
const ChatInput = memo(({ 
  selectedMode, 
  filters, 
  setFilters, 
  selectedText, 
  page, 
  selectedMenu = 0,
  setSelectedMode, 
  modeOptions, 
  setSelectedMenu, 
  displaySources, 
  setDisplaySources, 
  displayCarousel,
  setDisplayCarousel,
  setIsUpdatingFiles,
  devMode,
  setDevMode,
}: ChatInputProps) => {
  const { 
    chatItems,
    sendQuery, 
    abortQuery, 
    isLoadingQuery,
    isLoadingSelectedThread,
    isReformulation,
    selectedThread,
    appendOptimisticUserMessage,
    removeOptimisticUserMessages,
    appendOptimisticThoughtMessage,
    upsertOptimisticThoughtMessage,
    removeOptimisticThoughtMessages,
    interruptStreamForThread,
    resynthesizeExhaustiveFinal,
  } = useChatContext();
  const { addTask, tasks, setTasks } = useTasksContext();

  const exhaustiveCapablePage = useMemo(
    () => ["market", "mydeal", "legal", "oreima"].includes(page),
    [page]
  );

  const canResynthesizeExhaustiveFinal = useMemo(
    () =>
      chatItems.some((item) => isCarousel(item) && item.panels.length >= 2),
    [chatItems]
  );

  const onResynthesizeExhaustiveFinal = useCallback(() => {
    const tid = selectedThread?.id;
    if (!tid || !canResynthesizeExhaustiveFinal) return;
    void resynthesizeExhaustiveFinal(tid, page);
  }, [
    selectedThread?.id,
    canResynthesizeExhaustiveFinal,
    resynthesizeExhaustiveFinal,
    page,
  ]);

  // Verrou UI : tant qu'une tâche RAG est en cours pour CE thread,
  // on bloque la soumission (évite de reposer des questions).
  const activeTaskForSelectedThread = tasks.find(
    (t) =>
      t.type === TaskType.LEGAL_SEARCH &&
      t.threadId === selectedThread?.id &&
      (t.status === TaskStatus.PROCESSING ||
        t.status === TaskStatus.SUMMARIZING)
  );

  // Fallback (quand on vient de changer de page et que le sync ES n'a pas encore eu le temps de charger) :
  // si sessionStorage indique qu'un thread correspond à la dernière tâche exhaustive, on considère qu'on est "en cours".
  const pendingThreadIdFromSession =
    (() => {
      try {
        return sessionStorage.getItem(
          LEGAL_PENDING_TASK_THREAD_SESSION_KEY
        );
      } catch {
        return null;
      }
    })() || null;

  const isPendingThreadInProgress = Boolean(
    pendingThreadIdFromSession &&
      selectedThread?.id &&
      pendingThreadIdFromSession === selectedThread?.id
  );

  const isTaskInProgressForSelectedThread = Boolean(
    activeTaskForSelectedThread || isPendingThreadInProgress
  );
  const isInputLocked = isLoadingSelectedThread || isTaskInProgressForSelectedThread;
  /** Vrai reformulation (flux dédié) — ne pas confondre avec une tâche Legal/Exhaustive. */
  const reformulatingPlaceholder =
    Boolean(isReformulation && isLoadingSelectedThread);
  /** Stop : stream en cours OU tâche RAG / verrou saisie (sans afficher « Reformulating… »). */
  const showStopButton = reformulatingPlaceholder || isInputLocked;

  const runningTask = activeTaskForSelectedThread;
  const thinkingLabel = (() => {
    if (!runningTask) return "Georges is thinking…";
    const total = Math.max(1, runningTask.file.nbPages || 1);
    const processed = Math.max(0, Math.min(runningTask.file.nbProcessed || 0, total));
    const pct = Math.round((processed / total) * 100);
    return `Georges is thinking… ${pct}%`;
  })();

  const thinkingPlaceholderThreadIdRef = useRef<string | null>(null);
  useEffect(() => {
    const tid = selectedThread?.id;
    if (!tid) return;

    if (isTaskInProgressForSelectedThread) {
      // Tant que la tâche n’est pas terminée, on garde un placeholder si le SSE n’a pas encore
      // renvoyé ses premiers messages.
      if (!isLoadingQuery && thinkingPlaceholderThreadIdRef.current !== tid) {
        thinkingPlaceholderThreadIdRef.current = tid;
        appendOptimisticThoughtMessage(thinkingLabel, tid);
      } else if (!isLoadingQuery) {
        upsertOptimisticThoughtMessage(thinkingLabel, tid);
      }
      return;
    }

    // Plus de tâche active : on retire le placeholder.
    thinkingPlaceholderThreadIdRef.current = null;
    removeOptimisticThoughtMessages();
  }, [
    selectedThread?.id,
    isTaskInProgressForSelectedThread,
    isLoadingQuery,
    thinkingLabel,
    appendOptimisticThoughtMessage,
    upsertOptimisticThoughtMessage,
    removeOptimisticThoughtMessages,
  ]);

  const wasTaskRunningRef = useRef(false);
  const lastRunningThreadIdRef = useRef<string | null>(null);
  useEffect(() => {
    const isRunning = isTaskInProgressForSelectedThread;
    const wasRunning = wasTaskRunningRef.current;
    const currentThreadId = selectedThread?.id ?? null;
    wasTaskRunningRef.current = isRunning;
    if (isRunning && currentThreadId) {
      lastRunningThreadIdRef.current = currentThreadId;
    }
    // Ne fermer le panneau que si la tâche vient de se terminer sur le MÊME thread.
    // Si l'utilisateur navigue vers un autre thread, on ne touche pas au panneau courant.
    if (
      wasRunning &&
      !isRunning &&
      currentThreadId &&
      lastRunningThreadIdRef.current === currentThreadId
    ) {
      // Quand la recherche finit, on repasse sur la vue chat.
      setSelectedMenu(0);
      lastRunningThreadIdRef.current = null;
    }
  }, [isTaskInProgressForSelectedThread, selectedThread?.id, setSelectedMenu]);

  // Si la tâche apparaît dans `tasks` comme terminée, on vide le fallback sessionStorage
  // pour éviter que le verrou / placeholder restent bloqués.
  useEffect(() => {
    const tid = selectedThread?.id;
    if (!tid) return;

    if (pendingThreadIdFromSession !== tid) return;

    const t = tasks.find(
      (x) => x.type === TaskType.LEGAL_SEARCH && x.threadId === tid
    );
    if (!t) return;

    const stillRunning =
      t.status === TaskStatus.PROCESSING || t.status === TaskStatus.SUMMARIZING;
    if (!stillRunning) {
      clearPendingLegalTaskSession();
    }
  }, [
    selectedThread?.id,
    tasks,
    pendingThreadIdFromSession,
    clearPendingLegalTaskSession,
  ]);

  // Retire le placeholder dès qu’on reçoit un vrai message `thought` du backend
  // (sinon on risque de montrer deux "Georges is thinking…" côte à côte).
  useEffect(() => {
    const tid = selectedThread?.id;
    if (!tid) return;
    if (!isTaskInProgressForSelectedThread) return;

    const hasRealThought = chatItems.some((it) => {
      if (it.type !== "thought") return false;
      const id = (it as any).id;
      return id && !String(id).startsWith("optimistic-thought-");
    });

    if (hasRealThought) removeOptimisticThoughtMessages();
  }, [
    chatItems,
    selectedThread?.id,
    isTaskInProgressForSelectedThread,
    removeOptimisticThoughtMessages,
  ]);

  // Ajouter l'état local pour l'input
  const [localInput, setLocalInput] = useState("");
  const showModesIndicationsDefault = ["legal", "oreima"].includes(page);
  const [showModesIndications, setShowModesIndications] = useState(
    showModesIndicationsDefault
  );
  const [inputError, setInputError] = useState<string | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const isMobile = useIsMobile();
  const filtersRef = useRef(filters);
  const [showUploadProgress, setShowUploadProgress] = useState(false);
  const { instance } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const [processingStatus, setProcessingStatus] = useState<ProcessingStatus | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processedFiles, setProcessedFiles] = useState<ProcessingStatus[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [showProgressBar, setShowProgressBar] = useState(true);
  const [summarizingFiles, setSummarizingFiles] = useState<Set<string>>(new Set());
  /** Dernière requête legal avec tâche (fallback reload/session). */
  const pendingLegalTaskIdRef = useRef<string | null>(null);
  /** Toutes les tâches legal/exhaustive en cours dans CET onglet (support parallèle). */
  const pendingLegalTaskIdsRef = useRef<Set<string>>(new Set());
  const wasLoadingQueryRef = useRef(false);
  const [showAnswerFormatModal, setShowAnswerFormatModal] = useState(false);
  /** Texte figé au moment où l’utilisateur ouvre la modale (avant vidage du champ). */
  const [answerFormatModalQuery, setAnswerFormatModalQuery] = useState("");
  /** Search Infravia et ExhaustiveSearch sont mutuellement exclusifs ; hors Legal le toggle est OFF. */
  const [exhaustiveSearchEnabled, setExhaustiveSearchEnabled] = useState(false);
  /**
   * Ancien flux exhaustif (carousel + macro, `exhaustive_search_mode=classic`) + batch 1 doc.
   * Usage courant : laisser décoché → `wip` (pipeline tableau / panneaux RAG multi-doc).
   */
  const [exhaustiveSequentialClassic, setExhaustiveSequentialClassic] =
    useState(false);
  /** Thread figé pour la modale (évite deux UUID différents entre ouverture modale et envoi). */
  const pendingThreadForModalRef = useRef<string | null>(null);

  //const modesIndications = useMemo(() => content[page].containers.find((container) => container.modeValue === selectedMode), [page, selectedMode]);
  const { execute } = useFetchWithMsal();

  const abortAndCancelCurrentThreadTask = useCallback(async () => {
    const threadId = selectedThread?.id;
    if (!threadId) return;

    let taskIdFromSession: string | null = null;
    try {
      taskIdFromSession = sessionStorage.getItem(LEGAL_PENDING_TASK_SESSION_KEY);
    } catch {
      taskIdFromSession = null;
    }
    const primaryTaskId = resolveExhaustiveTaskIdForThread(
      tasks,
      threadId,
      pendingLegalTaskIdRef.current,
      taskIdFromSession
    );

    /**
     * Même flux que « Cancel » sur la carte tâche : POST d’abord pour que le serveur
     * (runner + ES `cancel_requested`) s’arrête ; puis coupure SSE + message système.
     */
    await cancelExhaustiveSearchJob(execute, {
      threadId,
      taskId: primaryTaskId,
      setTasks,
      pendingLegalTaskIdRef,
      pendingLegalTaskIdsRef,
    });

    interruptStreamForThread(threadId);
  }, [
    interruptStreamForThread,
    selectedThread?.id,
    tasks,
    execute,
    setTasks,
  ]);

  /** Legal : ExhaustiveSearch par défaut. Ne dépendre que de `page` pour ne pas réinitialiser le toggle sur market/mydeal. */
  useEffect(() => {
    if (page === "legal") {
      setExhaustiveSearchEnabled(true);
      setSelectedMode("SEARCH");
    } else {
      setExhaustiveSearchEnabled(false);
      setExhaustiveSequentialClassic(false);
    }
    // Uniquement au changement de page (pas à chaque rendu si setSelectedMode change de référence).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Fermer le menu latéral au démarrage de la saisie,
  // mais ne pas le fermer à chaque frappe ensuite : sinon ça empêche de rouvrir le panneau tant que le champ n'est pas vidé.
  const selectedMenuRef = useRef(selectedMenu);
  useEffect(() => {
    selectedMenuRef.current = selectedMenu;
  }, [selectedMenu]);

  /** Modale format pour ExhaustiveSearch (legal, oreima, market, mydeal). Search Infravia = envoi direct. */
  const shouldOpenAnswerFormatModal = useCallback(() => {
    return shouldShowAnswerFormatModal(page) && exhaustiveSearchEnabled;
  }, [page, exhaustiveSearchEnabled]);

  const prevLocalInputLenRef = useRef(localInput.length);
  useEffect(() => {
    const wasEmpty = prevLocalInputLenRef.current === 0;
    const isNonEmpty = localInput.length > 0;

    if (
      wasEmpty &&
      isNonEmpty &&
      selectedMode !== "MYGEORGES" &&
      selectedMenuRef.current !== 5
    ) {
      setSelectedMenu(0);
    }

    prevLocalInputLenRef.current = localInput.length;
  }, [localInput.length, selectedMode, setSelectedMenu]);

  const commitSend = useCallback(
    (
      answerFormat: AnswerFormat,
      customInstructions?: string,
      skipModalCheck = false,
      ragReasoningPace?: RagReasoningPace
    ) => {
      // Sécurité : ne pas relancer une question tant qu’un flux / job RAG est actif.
      if (isLoadingSelectedThread || isTaskInProgressForSelectedThread) return;
      if (showModesIndications) setShowModesIndications(false);
      if (!localInput?.trim()) return;
      // Garde-fou: si ExhaustiveSearch est actif, on impose le passage par la pop-up
      // même si l'appel ne vient pas de openSendFlow.
      if (!skipModalCheck && shouldOpenAnswerFormatModal()) {
        setAnswerFormatModalQuery(localInput.trim());
        setShowAnswerFormatModal(true);
        return;
      }

      const threadId =
        selectedThread?.id ||
        pendingThreadForModalRef.current ||
        uuidv4();
      pendingThreadForModalRef.current = null;
      const questionType = exhaustiveSearchEnabled
        ? "EXHAUSTIVE_SEARCH"
        : selectedMode;
      const payload: Record<string, unknown> = {
        query: localInput,
        filters: filtersRef.current,
        question_type: questionType,
        is_reformulated: false,
        page: page,
        threadId,
      };

      const useAnswerFormatFromModal = shouldOpenAnswerFormatModal();
      if (useAnswerFormatFromModal) {
        payload.answerFormat = answerFormat;
        if (answerFormat === ANSWER_FORMAT.CUSTOM) {
          payload.customOutputInstructions = customInstructions ?? "";
        }
        if (ragReasoningPace) {
          payload.ragReasoningPace = ragReasoningPace;
        }
      }
      // ExhaustiveSearch sans pop-up (pages hors Legal/RAG, cas rare) : synthèse par défaut.
      if (exhaustiveSearchEnabled && !useAnswerFormatFromModal) {
        payload.answerFormat = ANSWER_FORMAT.SYNTHESIS;
        delete (payload as any).customOutputInstructions;
      }

      if (exhaustiveSearchEnabled) {
        payload.exhaustiveSearchMode = exhaustiveSequentialClassic
          ? "classic"
          : "wip";
      }
      if (exhaustiveSearchEnabled && exhaustiveSequentialClassic) {
        payload.exhaustiveSequential = true;
      }

      if (shouldAttachLegalSearchTask(page, selectedMode, exhaustiveSearchEnabled)) {
        const paths = (filtersRef.current as { path?: string[] })?.path ?? [];
        const n = Array.isArray(paths) ? paths.length : 0;
        if (n > 300) {
          setInputError(
            "Trop de documents sélectionnés. Réduis la sélection à 300 documents maximum."
          );
          return;
        }
        setInputError(null);
        const { taskId, task: newTask } = createLegalSearchTaskFromQuery(
          localInput.trim(),
          n,
          threadId,
          { requiresServerResume: exhaustiveSearchEnabled }
        );
        // Important: pour l’affichage dans "Tasks" (Recherche Market/Deal/Legal/Oreima)
        // et pour les futures synchronisations, on renseigne le mode d’origine.
        newTask.page = page;
        newTask.mode = page;
        payload.taskId = taskId;
        setLegalActiveSseTaskId(taskId);
        setPendingLegalTaskId(taskId);
        // Permet d’afficher "Georges is thinking…" même si le sync ES n’a pas encore eu le temps de charger.
        setPendingLegalTaskThreadId(threadId);
        pendingLegalTaskIdRef.current = taskId;
        pendingLegalTaskIdsRef.current.add(taskId);
        try {
          addTask(newTask);
        } catch (e) {
          console.warn("addTask (Legal) a échoué, repli événement global", e);
        }
        emitLegalTaskAdded(newTask);
      }

      // Debug uniquement : ne pas utiliser `console.error` pour éviter de faire croire
      // qu'il s'agit d'une vraie erreur applicative.
      console.log("Sending query with payload:", payload);
      sendQuery(payload);
      setLocalInput("");
    },
    [
      localInput,
      selectedMode,
      exhaustiveSearchEnabled,
      sendQuery,
      page,
      addTask,
      showModesIndications,
      selectedThread,
      isLoadingSelectedThread,
      isTaskInProgressForSelectedThread,
      shouldOpenAnswerFormatModal,
      exhaustiveSequentialClassic,
    ]
  );

  const openSendFlow = useCallback(() => {
    // Empêche de reposer une question pendant un flux SSE ou une tâche RAG.
    if (isLoadingSelectedThread || isTaskInProgressForSelectedThread) return;
    if (!localInput?.trim()) return;
    if (shouldOpenAnswerFormatModal()) {
      const threadId =
        selectedThread?.id ||
        pendingThreadForModalRef.current ||
        uuidv4();
      pendingThreadForModalRef.current = threadId;
      setAnswerFormatModalQuery(localInput.trim());
      appendOptimisticUserMessage(localInput.trim(), threadId);
      setShowAnswerFormatModal(true);
      return;
    }
    commitSend(ANSWER_FORMAT.AUTO);
  }, [
    localInput,
    commitSend,
    shouldOpenAnswerFormatModal,
    selectedThread?.id,
    appendOptimisticUserMessage,
    isLoadingSelectedThread,
    isTaskInProgressForSelectedThread,
  ]);

  const handleAnswerFormatConfirm = useCallback(
    (
      fmt: AnswerFormat,
      customInstructions?: string,
      ragReasoningPace?: RagReasoningPace
    ) => {
      setShowAnswerFormatModal(false);
      commitSend(fmt, customInstructions, true, ragReasoningPace);
    },
    [commitSend]
  );

  const handleAnswerFormatModalHide = useCallback(() => {
    setShowAnswerFormatModal(false);
    pendingThreadForModalRef.current = null;
    removeOptimisticUserMessages();
  }, [removeOptimisticUserMessages]);

  /** Restaure l’id tâche après F5 (ref React remise à zéro). */
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(LEGAL_PENDING_TASK_SESSION_KEY);
      if (stored) {
        pendingLegalTaskIdRef.current = stored;
        pendingLegalTaskIdsRef.current.add(stored);
      }
    } catch {
      /* ignore */
    }
  }, []);

  /** Quand le back envoie un completed explicite, on retire l'id du pending local. */
  useEffect(() => {
    const onLegalProgress = (e: Event) => {
      const detail = (e as CustomEvent<Record<string, unknown>>).detail;
      const status = String(detail?.status ?? "").toLowerCase();
      if (status !== "completed") return;
      const tid = String(detail?.taskId ?? detail?.task_id ?? "");
      if (!tid) return;
      pendingLegalTaskIdsRef.current.delete(tid);
      if (pendingLegalTaskIdRef.current === tid) {
        pendingLegalTaskIdRef.current = null;
      }
    };
    window.addEventListener("infravia-legal-task-progress", onLegalProgress);
    return () =>
      window.removeEventListener("infravia-legal-task-progress", onLegalProgress);
  }, []);

  /** Fin du flux SSE : clôturer la barre (même si le back n’a pas envoyé `completed`). */
  useEffect(() => {
    const prev = wasLoadingQueryRef.current;
    wasLoadingQueryRef.current = isLoadingQuery;
    if (!prev || isLoadingQuery) return;
    // Tous les streams SSE sont terminés : finaliser proprement les tâches
    // encore "pending" qui n'ont pas reçu de completed explicite.
    const pendingIds = Array.from(pendingLegalTaskIdsRef.current);
    pendingIds.forEach((tid) => {
      const t = tasks.find((x) => x.id === tid && x.type === TaskType.LEGAL_SEARCH);
      if (!t) {
        pendingLegalTaskIdsRef.current.delete(tid);
        return;
      }
      // Pour les jobs serveurs (ExhaustiveSearch), la source de vérité reste l'API jobs.
      // Ne pas forcer un "completed" local juste parce que le SSE s'arrête (navigation/changement de mode).
      if (t.requiresServerResume === true) {
        return;
      }
      if (
        t.status === TaskStatus.COMPLETED ||
        t.status === TaskStatus.CANCELLED ||
        t.status === TaskStatus.ERROR
      ) {
        pendingLegalTaskIdsRef.current.delete(tid);
        return;
      }
      const total = Math.max(1, t.file.nbPages);
      const processed = Math.max(t.file.nbProcessed, 0);
      emitLegalTaskProgressCompleted(tid, Math.max(processed, 1), total);
      pendingLegalTaskIdsRef.current.delete(tid);
      if (pendingLegalTaskIdRef.current === tid) {
        pendingLegalTaskIdRef.current = null;
      }
    });
  }, [isLoadingQuery, tasks]);

  const reloadLegalBarReconciledRef = useRef(false);

  /**
   * Après rechargement : le SSE est coupé, la ref perdue, mais localStorage a la dernière progression SSE.
   * On applique un « completed » avec cette progression (sans forcer 100 % si incomplet).
   */
  useEffect(() => {
    if (reloadLegalBarReconciledRef.current) return;
    if (typeof performance === "undefined") return;
    const nav = performance.getEntriesByType?.(
      "navigation"
    )?.[0] as PerformanceNavigationTiming | undefined;
    if (nav?.type !== "reload") {
      reloadLegalBarReconciledRef.current = true;
      return;
    }
    let tid: string | null = null;
    try {
      tid = sessionStorage.getItem(LEGAL_PENDING_TASK_SESSION_KEY);
    } catch {
      reloadLegalBarReconciledRef.current = true;
      return;
    }
    if (!tid) {
      reloadLegalBarReconciledRef.current = true;
      return;
    }
    const t = tasks.find((x) => x.id === tid && x.type === TaskType.LEGAL_SEARCH);
    if (!t) return;
    if (
      t.status === TaskStatus.COMPLETED ||
      t.status === TaskStatus.CANCELLED ||
      t.status === TaskStatus.ERROR
    ) {
      reloadLegalBarReconciledRef.current = true;
      return;
    }
    reloadLegalBarReconciledRef.current = true;
    const total = Math.max(1, t.file.nbPages);
    const processed = Math.min(Math.max(0, t.file.nbProcessed), total);
    emitLegalTaskProgressCompleted(tid, Math.max(processed, 1), total);
  }, [tasks]);

  // Modifier handleInputChange pour utiliser setLocalInput au lieu de setQuery
  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (inputError) setInputError(null);
    setLocalInput(event.target.value);
  }, [inputError]);

  // Modifier handleKeyDown pour utiliser localInput
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        if (localInput && localInput.trim()) openSendFlow();
      }
    },
    [localInput, openSendFlow]
  );

  // Mis à jours des filtres
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  useEffect(() => {
    if(showModesIndications && chatItems.length > 0) setShowModesIndications(false);
  }, [chatItems.length]);

  useEffect(() => {
    setShowModesIndications(showModesIndicationsDefault);
  }, [showModesIndicationsDefault]);

  // Nouveau chat : restaurer le message d'accueil quand il n'y a plus de thread/message actif.
  useEffect(() => {
    const isFreshChat =
      !selectedThread?.id &&
      chatItems.length === 0 &&
      !isLoadingSelectedThread &&
      !isTaskInProgressForSelectedThread;
    if (isFreshChat && !showModesIndications && showModesIndicationsDefault) {
      setShowModesIndications(true);
    }
  }, [
    selectedThread?.id,
    chatItems.length,
    isLoadingSelectedThread,
    isTaskInProgressForSelectedThread,
    showModesIndications,
    showModesIndicationsDefault,
  ]);

  //  Mis à jours local input
  useEffect(() => {
    if (selectedText) {
      setLocalInput(selectedText);
    }
  }, [selectedText]);

  /*const handleFileUpload = async (fileInfo: FileUploadInfo) => {
    if (!fileInfo.files.length || !activeAccount) return;
    
    setShowUploadProgress(true);
    setIsProcessing(true);
    
    // Forcer un nouveau objet de statut pour déclencher le polling
    setProcessingStatus(prevStatus => {
      const existingFiles = prevStatus?.files || [];
      const newFiles = fileInfo.files.map(file => ({
        filename: file.filename,
        nbPages: file.totalPages,
        nbProcessed: 0,
        status: 'processing'
      }));
      
      return {
        files: [...existingFiles, ...newFiles]
      };
    });
  };

  const handleProcessingComplete = useCallback(async (completedStatus: ProcessingStatus) => {
    if (!completedStatus?.files || !activeAccount) return;
    
    console.log('🎯 handleProcessingComplete appelé avec:', completedStatus);
    
    // Mettre à jour immédiatement le processingStatus pour refléter les fichiers complétés
    setProcessingStatus(prevStatus => {
      if (!prevStatus?.files) return null;
      
      const remainingFiles = prevStatus.files.filter(file => 
        !completedStatus.files.some(completedFile => 
          completedFile.filename === file.filename
        )
      );
      
      if (remainingFiles.length === 0) {
        setIsProcessing(false);
        return null;
      }
      
      return { files: remainingFiles };
    });

    // Le reste du code pour la gestion des résumés et la mise à jour de processedFiles
    const allFiles = [...completedStatus.files, ...processedFiles.flatMap(status => status.files)];
    
    // Vérifier les résumés pour tous les fichiers
    for (const file of allFiles) {
      if (summarizingFiles.has(file.filename)) continue;

      try {
        const response = await execute('POST', endpointsCf.hasSummary, {
          file_name: file.filename,
          user_id: activeAccount.username,
          page: page,
          selected_mode: "MYGEORGES"
        });

        const hasSummary = response?.exists === true;

        if (!hasSummary) {
          // Vérifier à nouveau si le fichier n'est pas déjà en cours de résumé
          if (summarizingFiles.has(file.filename)) continue;
          
          // Ajouter le fichier à summarizingFiles AVANT de lancer le résumé
          setSummarizingFiles(prev => new Set([...prev, file.filename]));
          setIsUpdatingFiles(true);
          
          try {
            await execute('POST', endpointsCf.summarize, {
              filename: file.filename,
              userId: activeAccount.username,
              nPages: file.nbPages
            });
          } catch (error) {
            // En cas d'erreur, retirer le fichier de summarizingFiles
            setSummarizingFiles(prev => {
              const newSet = new Set(prev);
              newSet.delete(file.filename);
              return newSet;
            });
          }
        }
      } catch (error) {
        console.error(`❌ Erreur lors de la vérification du résumé pour ${file.filename}:`, error);
      }
    }

    // Mettre à jour l'historique des fichiers traités
    setProcessedFiles(prev => {
      const existingFilenames = new Set(prev.flatMap(status => 
        status.files.map(file => file.filename)
      ));
      
      const newFiles = completedStatus.files.filter(file => 
        !existingFilenames.has(file.filename)
      );
      
      if (newFiles.length === 0) return prev;
      
      return [{ files: newFiles }, ...prev];
    });
  }, [setFilters, filters, activeAccount, execute, processedFiles, summarizingFiles, page]);

  const handleStatusUpdate = useCallback((newStatus: ProcessingStatus) => {
    console.log('📊 Mise à jour du statut - données reçues:', newStatus);
    setProcessingStatus(prevStatus => {
      const existingFiles = prevStatus?.files || [];
      
      // S'assurer que les fichiers mis à jour conservent le bon format
      const updatedFiles = existingFiles.map(existingFile => {
        const updatedFile = newStatus.files.find(
          newFile => newFile.filename === existingFile.filename
        );
        
        if (updatedFile) {
          return {
            filename: updatedFile.filename,
            nbPages: updatedFile.nbPages,
            nbProcessed: updatedFile.nbProcessed,
            status: updatedFile.status
          };
        }
        return existingFile;
      });
      
      // Ajouter les nouveaux fichiers avec le bon format
      const newFiles = newStatus.files.filter(newFile => 
        !existingFiles.some(existingFile => existingFile.filename === newFile.filename)
      ).map(file => ({
        filename: file.filename,
        nbPages: file.nbPages,
        nbProcessed: file.nbProcessed,
        status: file.status
      }));
      
      const result = {
        files: [...updatedFiles, ...newFiles]
      };
      
      console.log('📊 Status mis à jour:', result);
      return result;
    });
  }, []);*/

  return (
    <>
    <AnswerFormatModal
      show={showAnswerFormatModal}
      questionPreview={answerFormatModalQuery}
      showCarouselToggle={exhaustiveSearchEnabled}
      displayCarousel={displayCarousel}
      onDisplayCarouselChange={setDisplayCarousel}
      onHide={handleAnswerFormatModalHide}
      onConfirm={handleAnswerFormatConfirm}
      devMode={devMode}
    />
    {showModesIndications && !isMobile && (
      <div className={styles.titleContainer}>
        <h1>How can I help you?</h1>
        <h4 style={{color: "grey"}}> 
          Georges, your <i className="bi bi-lock"></i> secure AI assistant
        </h4>
      </div>
    )}
    
    <div className={styles.chatContainer}>
      <div className={styles.chatFrame}>
        <div className={styles.chatInputContainer}>
          <Form className={styles.searchform}>
            <div className={styles.inputWrapper}>
              <MemoizedTextArea
                value={localInput}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                isLoadingQuery={isLoadingQuery}
                isReformulation={reformulatingPlaceholder}
                inputRef={inputRef}
              />
            </div>
            <div className={styles.buttonWrapper}>
              <MemoizedSendButton 
                isReformulation={showStopButton}
                onClick={openSendFlow}
                onAbort={() => void abortAndCancelCurrentThreadTask()}
              />
            </div>
          </Form>
          {inputError ? (
            <div
              style={{
                marginTop: "0.4rem",
                color: "#b42318",
                fontSize: "0.85rem",
                width: "100%",
                textAlign: "center",
              }}
            >
              {inputError}
            </div>
          ) : null}
        </div>

        {/* Modes */}
        <div className={styles.modesContainer}>
          <MemoizedControls 
            selectedMode={selectedMode}
            modeOptions={modeOptions}
            exhaustiveSearchEnabled={exhaustiveSearchEnabled}
            setExhaustiveSearchEnabled={setExhaustiveSearchEnabled}
            setSelectedMode={setSelectedMode}
            setSelectedMenu={setSelectedMenu}
            isMobile={isMobile}
            optionIcons={chatInputConfig.optionIcons}
            page={page}
            displaySources={displaySources}
            setDisplaySources={setDisplaySources}
            devMode={devMode}
            setDevMode={setDevMode}
            exhaustiveSequentialClassic={exhaustiveSequentialClassic}
            setExhaustiveSequentialClassic={setExhaustiveSequentialClassic}
            displayCarousel={displayCarousel}
            setDisplayCarousel={setDisplayCarousel}
            exhaustiveCapablePage={exhaustiveCapablePage}
            onResynthesizeExhaustiveFinal={onResynthesizeExhaustiveFinal}
            canResynthesizeExhaustiveFinal={canResynthesizeExhaustiveFinal}
            resynthesizeFinalBusy={isLoadingSelectedThread}
          />
        </div>
      </div>
    </div>
    </>
  );
});

//Sauvegarde des exemples de requêtes
{/*
{showModesIndications && !isMobile && modesIndications?.squares?.map((square) => (
  <div 
    key={`${square.title}-${square.text}`} 
    className={styles.modesIndicationsItem} 
    onClick={() => setLocalInput(square.text)}
  >
    <i className={square.icon} style={{color: "grey", paddingLeft: 10, paddingRight: 10, paddingTop: 2}}></i>
    <div style={{display: "flex", flexDirection: "column", alignItems: "flex-start"}}>
      <p style={{ fontSize: "0.8rem", color: "grey", fontWeight: "bold"}}>{square.title}</p>
      <p style={{ fontSize: "0.8rem", color: "grey"}}>{square.text}</p>
    </div>
  </div>
))}*/}

export default ChatInput;


